import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** The ramp's steps (index.css › --chart-seq-*), light for little. */
const RAMP = [1, 2, 3, 4, 5, 6, 7] as const;
const STEPS = RAMP.length;

/** The step a share 0–1 falls in, 1 to 7; none for a share of 0. */
function step(share: number): number {
  return share <= 0 ? 0 : Math.min(STEPS, Math.ceil(share * STEPS));
}

export interface HeatRow {
  key: string;
  label: ReactNode;
  /** E.g. the cohort's size. */
  detail?: ReactNode;
  /** Each cell's share 0–1 for its shade and its text; null for no cell. */
  cells: readonly ({ share: number; text: string; title?: string } | null)[];
}

/**
 * A table whose cells are shaded by their share (dataviz: sequential, one hue): the cohort table
 * and the hour-of-week heatmap. The text sits in each cell in an ink that clears 4.5:1 on its
 * step, so the colour never carries the value alone; the scale is drawn below.
 */
export function HeatTable({
  columns,
  rows,
  caption,
  rowHeader,
  detailHeader,
  legend,
  compact = false,
}: {
  columns: readonly { key: string; label: ReactNode }[];
  rows: readonly HeatRow[];
  caption: string;
  rowHeader: ReactNode;
  detailHeader?: ReactNode;
  /** The scale's ends, e.g. "0 %" and "100 %". */
  legend: [ReactNode, ReactNode];
  compact?: boolean;
}) {
  return (
    <div>
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full border-separate border-spacing-0.5 text-xs">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="text-muted-foreground">
              <th scope="col" className="px-1.5 py-1 text-left font-medium">
                {rowHeader}
              </th>
              {detailHeader ? (
                <th scope="col" className="px-1.5 py-1 text-right font-medium">
                  {detailHeader}
                </th>
              ) : null}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-1 py-1 text-center font-medium tabular"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row" className="px-1.5 py-1 text-left font-medium whitespace-nowrap">
                  {row.label}
                </th>
                {detailHeader ? (
                  <td className="px-1.5 py-1 text-right text-muted-foreground tabular">
                    {row.detail}
                  </td>
                ) : null}
                {columns.map((column, i) => {
                  const cell = row.cells[i];
                  if (!cell) return <td key={column.key} />;
                  const s = step(cell.share);
                  return (
                    <td
                      key={column.key}
                      title={cell.title}
                      className={cn(
                        "rounded-[3px] text-center tabular",
                        compact ? "h-6 min-w-6 px-0.5" : "h-7 min-w-11 px-1",
                        s === 0 && "bg-muted text-muted-foreground",
                      )}
                      style={
                        s > 0
                          ? {
                              background: `var(--chart-seq-${s})`,
                              color: `var(--chart-seq-ink-${s})`,
                            }
                          : undefined
                      }
                    >
                      {cell.text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground" aria-hidden>
        {legend[0]}
        <span className="flex">
          {RAMP.map((n) => (
            <span
              key={n}
              className="h-2.5 w-4 first:rounded-l-sm last:rounded-r-sm"
              style={{ background: `var(--chart-seq-${n})` }}
            />
          ))}
        </span>
        {legend[1]}
      </div>
    </div>
  );
}
